import type { JSX } from 'react';
import { useState } from 'react';
import type { WireArtifact } from '@logistics/contracts';
import { useToast } from '@/shell/toast';
import { Truncated } from './parts';
import type { TableSort } from './table-tools';
import { nextSort, sortMark, sortRows, toCsv } from './table-tools';

type Table = Extract<WireArtifact, { kind: 'table' }>;

/**
 * Rows as the records gave them, one cell per column, each cut to one line with
 * a tooltip. A column head sorts (none → ascending → descending), and the rows
 * on screen can be copied as CSV — both client-side, over what was retrieved.
 */
export function TableArtifact({ artifact }: { artifact: Table }): JSX.Element {
  const [sort, setSort] = useState<TableSort | undefined>(undefined);
  const toast = useToast();
  const cols = `repeat(${Math.max(1, artifact.columns.length)}, minmax(0, 1fr))`;
  const rows = sortRows(artifact.rows, sort);

  const copy = (): void => {
    void copyCsv(toCsv(artifact.columns, rows), toast);
  };

  return (
    <div>
      <div className="mb-2.5 flex items-center justify-between gap-3">
        <span className="text-mute font-mono text-[10px] tracking-[.08em]">
          {artifact.rows.length} {artifact.rows.length === 1 ? 'ROW' : 'ROWS'}
        </span>
        <button
          type="button"
          onClick={copy}
          disabled={artifact.rows.length === 0}
          className="border-line text-ink hover:bg-surf2 rounded-[9px] border bg-transparent px-3 py-1.5 text-[12.5px] transition-colors disabled:opacity-50"
        >
          Copy as CSV
        </button>
      </div>
      <div className="border-line bg-surf overflow-hidden rounded-xl border">
        <div
          className="border-line text-mute grid gap-2.5 border-b px-3.5 py-2.5 font-mono text-[10px] tracking-[.08em]"
          style={{ gridTemplateColumns: cols }}
        >
          {artifact.columns.map((c, i) => (
            <HeadCell
              key={i}
              label={c}
              mark={sortMark(sort, i)}
              onClick={() => setSort((current) => nextSort(current, i))}
            />
          ))}
        </div>
        {rows.map((row, i) => (
          <div
            key={i}
            className="border-line grid items-center gap-2.5 border-b px-3.5 py-2.5 text-[12.5px] last:border-b-0"
            style={{ gridTemplateColumns: cols }}
          >
            {row.map((cell, j) => (
              <span key={j} title={cell} className="min-w-0 truncate">
                {cell}
              </span>
            ))}
          </div>
        ))}
        {rows.length === 0 && <div className="text-mute px-3.5 py-4 text-[12.5px]">No rows.</div>}
      </div>
      {artifact.truncated && <Truncated what={`${artifact.rows.length} rows`} />}
    </div>
  );
}

function HeadCell({
  label,
  mark,
  onClick,
}: {
  label: string;
  mark: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      title={`Sort by ${label}`}
      className="hover:text-ink flex min-w-0 items-center gap-1 border-0 bg-transparent p-0 text-left font-mono text-[10px] tracking-[.08em] text-inherit transition-colors"
    >
      <span className="truncate">{label.toUpperCase()}</span>
      {mark !== '' && <span className="text-acc flex-none">{mark}</span>}
    </button>
  );
}

/** The clipboard is missing in an insecure context and can be refused in any. */
async function copyCsv(csv: string, toast: (text: string) => void): Promise<void> {
  try {
    await navigator.clipboard.writeText(csv);
    toast('Table copied as CSV');
  } catch {
    toast('Could not reach the clipboard');
  }
}
